import { Button, type ModalHandle } from "@9amhealth/shared";
import styled from "@emotion/styled";
import type { FC, RefObject } from "react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { IconCheckmarkCompleted } from "src/constants/icons";
import createTrackEvent from "src/lib/createTrackEvent";
import HistoryBloc from "src/state/HistoryBloc/HistoryBloc";
import type { QuestionnaireState } from "src/state/QuestionnaireCubit/QuestionnaireState";
import { tracker, useBloc } from "src/state/state";
import Translate from "src/ui/components/Translate/Translate";
import CustomSuspense from "../CustomSuspense/CustomSuspense";
import InDialog from "../InDialog/InDialog";
import CustomQuestionnaire from "../CustomQuestionnaire/CustomQuestionnaire";

export const CenterQuestionnaireContent = styled.div`
  label: CenterQuestionnaireContent;
  display: grid;
  place-items: center;
  min-height: 100%;
  width: 100%;

  > div {
    width: 100%;
  }
`;

export const CenterDialogContent = styled.div`
  border-radius: 0.6em;
  width: 90vw;
  max-width: 580px;
  text-align: center;
  margin: 0 auto;

  p {
  }

  a {
    color: var(--color-sunrise-blue);
  }

  .icon-checkmark {
    width: 3rem;
    height: auto;
    border: 2px solid var(--color-charcoal-60);
    border-radius: 50%;
  }
`;

const InAppQuestionnaire: FC<{
  returnUrl?: string;
}> = ({ returnUrl }) => {
  const [historyState] = useBloc(HistoryBloc);
  const [questionnaireData, setQuestionnaireData] = useState<
    QuestionnaireState | undefined
  >(undefined);
  const { id = "" } = useParams<{ id?: string }>();
  const [query] = useSearchParams();
  const questionIdQuery = query.get("questionnaireId") ?? "";
  const title = query.get("title");
  const preventLoadingUserAnswers = query.get("preventLoadingUserAnswers");
  const fullscreen = query.get("fullscreen");
  const dialogType = query.get("dialog-type") ?? "";

  const [completed, setCompleted] = useState(false);
  const [overlayRef, setOverlayRef] = useState<RefObject<ModalHandle | null>>();
  const navigate = useNavigate();

  const preOpenAppPage = useMemo<string>(() => {
    if (returnUrl) {
      return returnUrl;
    }
    // find last url that starts with "/app", but does not include "/app/questionnaire"
    const justPathnames = historyState.history
      .map(({ pathname }) => pathname)
      .reverse();
    const appPage = justPathnames.find(
      (url) => url.startsWith("/app") && !url.startsWith("/app/questionnaire")
    );

    return appPage ?? "/app";
  }, [historyState]);

  const handleClose = () => {
    if (overlayRef?.current) {
      overlayRef.current.dismiss();
    }
    navigate(preOpenAppPage);
  };

  useEffect(() => {
    if (!questionnaireData?.formMeta.title) {
      return;
    }

    tracker.track(createTrackEvent("In-App Questionnaire Opened"), {
      data: {
        "Questionnaire ID": questionnaireData.formId,
        "Questionnaire Title": questionnaireData.formMeta.title
      }
    });
  }, [questionnaireData?.formId]);

  const useQuestionnaireId = questionIdQuery || id;
  const titleFormatted = title === "undefined" ? undefined : title;

  return (
    <InDialog
      onClose={handleClose}
      title={titleFormatted ?? "Questionnaire"}
      setRef={setOverlayRef}
      returnUrl={returnUrl ?? preOpenAppPage}
      backdropDismiss={false}
      height={fullscreen ? "100vh" : "800px"}
      width={fullscreen ? "100vw" : "700px"}
      dialogType={dialogType}
    >
      <CenterQuestionnaireContent>
        {completed && (
          <div>
            <CenterDialogContent>
              <nine-heading>
                <IconCheckmarkCompleted />
                <>
                  <nine-spacer s="md"></nine-spacer>
                  <h4>
                    <Translate msg="questionnaire.completed" />
                  </h4>
                  <nine-spacer s="md"></nine-spacer>
                  <p className="m0 color-c-80">
                    <Translate msg="questionnaire.thankYouMessage" />
                  </p>
                </>
              </nine-heading>
              <nine-spacer s="xl"></nine-spacer>
              <Button onPress={handleClose} theme="charcoal" center>
                <Translate msg="button.backToApp" />
              </Button>
            </CenterDialogContent>
          </div>
        )}
        {!completed && (
          <CustomSuspense>
            <CustomQuestionnaire
              preventLoadingUserAnswers={Boolean(preventLoadingUserAnswers)}
              autoSkip={false}
              resetScrollOnEachStep
              onQuestionnaireData={setQuestionnaireData}
              id={useQuestionnaireId}
              useRouting
              onLastStepCompleted={() => setCompleted(true)}
            />
          </CustomSuspense>
        )}
      </CenterQuestionnaireContent>
    </InDialog>
  );
};

export default InAppQuestionnaire;
